/**
 * Endpoint del portale acquisti.
 *
 * Il client HTTP e' quello condiviso (`@shared/services/api`): gestisce token, refresh su 401 e
 * redirect a /login. Qui ci sono solo gli indirizzi, che sono l'unica cosa specifica di questo
 * portale.
 *
 * Tre di questi endpoint stanno **fuori** da `/api/acquisti`: RSA, tipologie di fornitura e i
 * criteri di valutazione sono anagrafiche di manutenzioni, non di acquisti, e il ruolo Ufficio
 * Acquisti ha gia' le sezioni che servono a leggerle.
 */

import api from "@shared/services/api";

const qs = (params) => {
  const puliti = Object.fromEntries(
    Object.entries(params || {}).filter(
      ([, v]) => v !== undefined && v !== null && v !== "",
    ),
  );
  const s = new URLSearchParams(puliti).toString();
  return s ? `?${s}` : "";
};

/** `/api/acquisti/contratti` — l'archivio contrattuale, censimento compreso. */
export const contrattiApi = {
  list: (params) => api.get(`/api/acquisti/contratti${qs(params)}`),
  get: (id) => api.get(`/api/acquisti/contratti/${id}`),
  create: (data) => api.post("/api/acquisti/contratti", data),
  update: (id, data) => api.put(`/api/acquisti/contratti/${id}`, data),
  remove: (id) => api.delete(`/api/acquisti/contratti/${id}`),

  // Chiusura: anteprima del testo PEC, lettera PDF (assegna il protocollo), registrazione dell'invio.
  chiusura: (id, params) =>
    api.get(`/api/acquisti/contratti/${id}/chiusura${qs(params)}`),
  chiusuraPdf: (id, data) =>
    api.post(`/api/acquisti/contratti/${id}/chiusura/pdf`, data),
  registraChiusura: (id, data) =>
    api.post(`/api/acquisti/contratti/${id}/chiusura/registra`, data),
  documento: (id, fileId) =>
    `/api/acquisti/contratti/${id}/documenti/${fileId}`,
};

/** `/api/acquisti/capitolati` — testata, voci, versioning e lock. */
export const capitolatiApi = {
  list: (params) => api.get(`/api/acquisti/capitolati${qs(params)}`),
  get: (id) => api.get(`/api/acquisti/capitolati/${id}`),
  create: (data) => api.post("/api/acquisti/capitolati", data),
  update: (id, data) => api.put(`/api/acquisti/capitolati/${id}`, data),
  remove: (id) => api.delete(`/api/acquisti/capitolati/${id}`),
  attiva: (id) => api.post(`/api/acquisti/capitolati/${id}/attiva`, {}),
  nuovaVersione: (id) =>
    api.post(`/api/acquisti/capitolati/${id}/nuova-versione`, {}),
  blocca: (id, motivo) =>
    api.post(`/api/acquisti/capitolati/${id}/blocca`, { motivo }),
  sblocca: (id) => api.delete(`/api/acquisti/capitolati/${id}/blocca`),

  creaVoce: (id, data) => api.post(`/api/acquisti/capitolati/${id}/voci`, data),
  aggiornaVoce: (id, voceId, data) =>
    api.put(`/api/acquisti/capitolati/${id}/voci/${voceId}`, data),
  eliminaVoce: (id, voceId) =>
    api.delete(`/api/acquisti/capitolati/${id}/voci/${voceId}`),

  // Componenti di un master (ADR-0007)
  creaComponente: (id, data) =>
    api.post(`/api/acquisti/capitolati/${id}/componenti`, data),
  aggiornaComponente: (id, componenteId, data) =>
    api.put(`/api/acquisti/capitolati/${id}/componenti/${componenteId}`, data),
  eliminaComponente: (id, componenteId) =>
    api.delete(`/api/acquisti/capitolati/${id}/componenti/${componenteId}`),
  aggiornaComponenti: (id) =>
    api.post(`/api/acquisti/capitolati/${id}/aggiorna-componenti`, {}),
};

/** `/api/acquisti/gare` — gare, inviti, invio, offerte. Sprint 04. */
export const gareApi = {
  list: (params) => api.get(`/api/acquisti/gare${qs(params)}`),
  get: (id) => api.get(`/api/acquisti/gare/${id}`),
  propostaInvitati: (params) =>
    api.get(`/api/acquisti/gare/proposta-invitati${qs(params)}`),
  create: (data) => api.post("/api/acquisti/gare", data),
  update: (id, data) => api.put(`/api/acquisti/gare/${id}`, data),
  remove: (id) => api.delete(`/api/acquisti/gare/${id}`),
  invia: (id) => api.post(`/api/acquisti/gare/${id}/invia`, {}),
  chiudiRaccolta: (id) =>
    api.post(`/api/acquisti/gare/${id}/chiudi-raccolta`, {}),
  riapriRaccolta: (id) =>
    api.post(`/api/acquisti/gare/${id}/riapri-raccolta`, {}),
  annulla: (id, motivo) =>
    api.post(`/api/acquisti/gare/${id}/annulla`, { motivo }),
  richiestaPdf: (id) => `/api/acquisti/gare/${id}/richiesta.pdf`,

  aggiungiInvito: (id, data) =>
    api.post(`/api/acquisti/gare/${id}/inviti`, data),
  aggiornaInvito: (id, invitoId, data) =>
    api.put(`/api/acquisti/gare/${id}/inviti/${invitoId}`, data),
  rimuoviInvito: (id, invitoId) =>
    api.delete(`/api/acquisti/gare/${id}/inviti/${invitoId}`),
  declinaInvito: (id, invitoId) =>
    api.post(`/api/acquisti/gare/${id}/inviti/${invitoId}/declina`, {}),
  inviaInvito: (id, invitoId) =>
    api.post(`/api/acquisti/gare/${id}/inviti/${invitoId}/invia`, {}),

  creaOfferta: (id, data) => api.post(`/api/acquisti/gare/${id}/offerte`, data),
  offerta: (id, offertaId) =>
    api.get(`/api/acquisti/gare/${id}/offerte/${offertaId}`),
  aggiornaOfferta: (id, offertaId, data) =>
    api.put(`/api/acquisti/gare/${id}/offerte/${offertaId}`, data),
  salvaVoci: (id, offertaId, voci) =>
    api.put(`/api/acquisti/gare/${id}/offerte/${offertaId}/voci`, { voci }),
  rilancio: (id, offertaId, data = {}) =>
    api.post(`/api/acquisti/gare/${id}/offerte/${offertaId}/rilancio`, data),
  documentiOfferta: (id, offertaId) =>
    `/api/acquisti/gare/${id}/offerte/${offertaId}/documenti`,
};

/** `/api/acquisti/analisi` — comparazione, snapshot, approvazione, contratto, storico. Sprint 05. */
export const analisiApi = {
  list: (params) => api.get(`/api/acquisti/analisi${qs(params)}`),
  calcolo: (garaId) => api.get(`/api/acquisti/analisi/gara/${garaId}/calcolo`),
  perGara: (garaId) => api.get(`/api/acquisti/analisi/gara/${garaId}`),
  crea: (garaId, proposta) =>
    api.post(`/api/acquisti/analisi/gara/${garaId}`, { proposta }),
  get: (id) => api.get(`/api/acquisti/analisi/${id}`),
  aggiornaProposta: (id, proposta) =>
    api.put(`/api/acquisti/analisi/${id}`, { proposta }),
  approva: (id, data) => api.post(`/api/acquisti/analisi/${id}/approva`, data),
  pdf: (id) => `/api/acquisti/analisi/${id}/pdf`,
  generaContratto: (id) =>
    api.post(`/api/acquisti/analisi/${id}/genera-contratto`, {}),
  storicoTipologia: (id) =>
    api.get(`/api/acquisti/analisi/storico/tipologia/${id}`),
  storicoFornitore: (id) =>
    api.get(`/api/acquisti/analisi/storico/fornitore/${id}`),
};

/** `/api/acquisti/fornitori` — anagrafica e livello di riservatezza. */
export const fornitoriApi = {
  list: (params) => api.get(`/api/acquisti/fornitori${qs(params)}`),
  impronta: (id) => api.get(`/api/acquisti/fornitori/${id}/impronta`),
  cambiaLivello: (id, livello) =>
    api.patch(`/api/acquisti/fornitori/${id}/livello`, { livello }),
  aggiornaAnagrafica: (id, data) =>
    api.patch(`/api/acquisti/fornitori/${id}/anagrafica`, data),
};

/** `/api/acquisti/albo` — la N:N fornitore ↔ tipologia di fornitura, in entrambe le direzioni. */
export const alboApi = {
  perFornitore: (id) => api.get(`/api/acquisti/albo/fornitore/${id}`),
  perCategoria: (id) => api.get(`/api/acquisti/albo/categoria/${id}`),
  salvaPerFornitore: (id, tipologiaIds) =>
    api.put(`/api/acquisti/albo/fornitore/${id}`, { tipologiaIds }),
  salvaPerCategoria: (id, fornitoreIds) =>
    api.put(`/api/acquisti/albo/categoria/${id}`, { fornitoreIds }),
};

/**
 * Anagrafiche condivise con manutenzioni.
 *
 * `/api/criteri-valutazione` e' l'unica che rompe la forma delle altre: risponde con un **array
 * nudo** di record Dataverse grezzi (`phc_criterivalutazioneid`, `phc_name`), non con
 * `{ value: [{ id, nome }] }`. Si normalizza qui, una volta, invece di far conoscere l'eccezione
 * a ogni pagina che la usa.
 */
export const anagraficheApi = {
  rsa: () => api.get("/api/rsa"),
  tipologieFornitura: () => api.get("/api/tipologie-fornitura"),
  criteriValutazione: () =>
    api.get("/api/criteri-valutazione").then((res) => ({
      value: (Array.isArray(res) ? res : res?.value || []).map((c) => ({
        id: c.phc_criterivalutazioneid,
        nome: c.phc_name,
        macrocategoria: c.phc_macrocategoria,
      })),
    })),
};

export default {
  contrattiApi,
  capitolatiApi,
  gareApi,
  analisiApi,
  fornitoriApi,
  alboApi,
  anagraficheApi,
};
